import { BaseService } from "./base";
import { Employee } from "../models/employee";
import { ResourceType } from "../models/resourceType";

import { ApiRequest } from "../lib/request";
import { ApiResponse, ApiListResponse } from "../lib/response";
import { Role } from "../models/role";

export class EmployeeService extends BaseService<Employee> {
  override model = Employee;
  override modelName = 'employee';
  override modelLabel = 'Employee';

  async employees(  
    page: number = 1,
    limit: number = 100,
    modifiedSince?: Date
  ): Promise<ApiListResponse<Employee>> {
    const query: Record<string, string> = {
      filter: 'employee',
      page: page.toString(),
      limit: limit.toString(),
    };

    const headers: Record<string, string> = {};

    if (modifiedSince) {
      query.modifiedSince = (modifiedSince.getTime() / 1000).toString();
      headers['If-Modified-Since'] = modifiedSince.toUTCString();
    }

    const request = new ApiRequest({
      method: 'GET',
      url: this.modelBaseUrl,
      query,
      headers,
    });

    return await this.requestList<Employee>(request, this.model)
  }
  
  async resources(
    page: number = 1,
    limit: number = 100,
    modifiedSince?: Date
  ): Promise<ApiListResponse<Employee>> {
    const query: Record<string, string> = {
      filter: 'resource',
      page: page.toString(),
      limit: limit.toString(),
    };

    const headers: Record<string, string> = {};

    if (modifiedSince) {
      query.modifiedSince = (modifiedSince.getTime() / 1000).toString();
      headers['If-Modified-Since'] = modifiedSince.toUTCString();
    }

    const request = new ApiRequest({
      method: 'GET',
      url: this.modelBaseUrl,
      query,
      headers,
    });

    return await this.requestList<Employee>(request, this.model)
  }
  
  async addPhoto(
    employee: Employee,
    image: File,
    onUploadProgress?: (progressEvent: import('axios').AxiosProgressEvent) => void,
  ): Promise<ApiResponse<Employee>> {
    const formData = new FormData();
    formData.append('image', image);

    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/${employee.idEmployee}/addPhoto`,
      data: formData,
      onUploadProgress,
    });

    return await this.requestItem<Employee>(request, this.model);
  }

  async deletePhoto(
    employee: Employee
  ): Promise<ApiResponse<Employee>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/${employee.idEmployee}/deletePhoto`,
    });

    return await this.requestItem<Employee>(request, this.model);
  }

  override async delete(
    employee: Employee,
    retainOldAppointments: boolean = true
  ): Promise<ApiResponse<Employee>> {
    const request = new ApiRequest({
      method: 'DELETE',
      url: `${this.modelBaseUrl}/${employee.idEmployee}`,
      query: { retain: retainOldAppointments ? '1' : '0' },
    });

    return await this.requestItem<Employee>(request, this.model);
  }

  async resetPassword({
    employee,
    password,
    confirm,
    sendEmail,
    revokeSessions,
  }: {
    employee: Employee;
    password: string;
    confirm: string;
    sendEmail: boolean;
    revokeSessions: boolean;
  }): Promise<ApiResponse<Employee>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/${employee.idEmployee}/resetPassword`,
      query: {
        password,
        confirm,
        sendEmail: sendEmail ? '1' : '0',
        revokeSessions: revokeSessions ? '1' : '0',
      },
    });

    return await this.requestItem<Employee>(request, this.model);
  }

  async resetPin(
    employee: Employee,
    pin: string,
    confirm: string
  ): Promise<ApiResponse<Employee>> {
    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/${employee.idEmployee}/resetPin`,
      data: { pin, confirm },
    });

    return await this.requestItem<Employee>(request, this.model);
  }

  async assignRole(
    employee: Employee,
    role: Role
  ): Promise<ApiResponse<Employee>> {
    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/${employee.idEmployee}/assignRole`,
      data: {
        roleType: role?.type || 'custom',
        idCustomRole: role?.idCustomRole,
      },
    });

    return await this.requestItem<Employee>(request, this.model);
  }

  /**
   * Transfer location ownership to another employee. Backend route is the
   * collection-level `POST /employee/changeOwner` — the target employee is
   * identified by `newOwner` in the body, not by a path id. Caller still
   * passes `employee` for the existing argument shape; if `newOwner` is
   * omitted we fall back to the employee's id so old callsites keep working.
   */
  async changeOwner({
    employee,
    token,
    code,
    newOwner,
  }: {
    employee: Employee,
    token?: string;
    code?: string;
    newOwner?: string;
  }): Promise<ApiResponse<Employee>> {
    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/changeOwner`,
      data: {
        token,
        code,
        newOwner: newOwner ?? String(employee.idEmployee),
      },
    });

    return await this.requestItem<Employee>(request, this.model);
  }

  async changeEmail({
    employee,
    token,
    code,
    email,
    firstName,
    lastName,
  }: {
    employee: Employee,
    token?: string;
    code?: string;
    email?: string;
    firstName?: string;
    lastName?: string;
  }): Promise<ApiResponse<Employee>> {
    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/${employee.idEmployee}/changeEmail`,
      data: { token, code, email, firstName, lastName },
    });

    return await this.requestItem<Employee>(request, this.model);
  }

  async updatePrices(
    employee: Employee,
    updateFutureReservations: boolean = false
  ): Promise<ApiResponse<Employee>> {
    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/${employee.idEmployee}/updatePrices`,
      query: { updateFutureReservations: updateFutureReservations ? '1' : '0' },
    });

    return await this.requestItem<Employee>(request, this.model);
  }
}